import { BillingConfig, CatalogueItem } from '../types/billing'

/** Minimal RFC-4180 CSV parser (quoted fields, escaped quotes, CRLF). */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let quoted = false
  const src = text.replace(/^\ufeff/, '')
  for (let i = 0; i < src.length; i++) {
    const c = src[i]
    if (quoted) {
      if (c === '"' && src[i + 1] === '"') { field += '"'; i++ }
      else if (c === '"') quoted = false
      else field += c
    } else if (c === '"') quoted = true
    else if (c === ',') { row.push(field); field = '' }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++
      row.push(field); field = ''
      if (row.some((v) => v.trim())) rows.push(row)
      row = []
    } else field += c
  }
  row.push(field)
  if (row.some((v) => v.trim())) rows.push(row)
  return rows
}

export const itemCsvColumns = ['code', 'name', 'description', 'category', 'department', 'type', 'unit', 'price', 'tax_code', 'tax_inclusive', 'discount_allowed', 'charge_type'] as const

const yes = (v: string) => /^(y|yes|true|1)$/i.test(v.trim())
const chargeTypes: CatalogueItem['sourceType'][] = ['consultation', 'lab', 'radiology', 'procedure', 'pharmacy', 'other']

/** Converts CSV rows into item records. Stock items cannot be imported (they need an inventory link). */
export function rowsToItems(config: BillingConfig, rows: string[][]): { items: Omit<CatalogueItem, 'id' | 'createdAt' | 'updatedAt' | 'priceHistory'>[]; error?: string } {
  if (rows.length < 2) return { items: [], error: 'The file needs a header row and at least one item.' }
  const header = rows[0].map((h) => h.trim().toLowerCase())
  const missing = ['code', 'name', 'category', 'department', 'unit', 'price'].filter((c) => !header.includes(c))
  if (missing.length) return { items: [], error: `Missing column(s): ${missing.join(', ')}. Download the template for the expected layout.` }
  const get = (r: string[], key: string) => (header.indexOf(key) >= 0 ? (r[header.indexOf(key)] ?? '').trim() : '')
  const items: Omit<CatalogueItem, 'id' | 'createdAt' | 'updatedAt' | 'priceHistory'>[] = []
  for (const [i, r] of rows.slice(1).entries()) {
    const line = i + 2
    if (/^stock$/i.test(get(r, 'type'))) return { items: [], error: `Row ${line}: stock items must be added one at a time so they can be linked to Pharmacy Inventory.` }
    const taxCode = get(r, 'tax_code')
    const tax = taxCode ? config.taxes.rates.find((t) => (t.code ?? '').toUpperCase() === taxCode.toUpperCase()) : config.taxes.rates.find((t) => t.id === config.taxes.defaultTaxId)
    if (!tax) return { items: [], error: `Row ${line}: tax code "${taxCode}" does not exist in Tax settings.` }
    const price = Number(get(r, 'price').replace(/[^0-9.-]/g, ''))
    if (!Number.isFinite(price) || price < 0) return { items: [], error: `Row ${line}: price "${get(r, 'price')}" is not a valid amount.` }
    const category = get(r, 'category')
    if (!config.catalogue.categories.includes(category)) return { items: [], error: `Row ${line}: category "${category}" is not in Items & pricing settings.` }
    const department = get(r, 'department')
    if (!config.catalogue.departments.includes(department)) return { items: [], error: `Row ${line}: department "${department}" is not in Items & pricing settings.` }
    const unit = get(r, 'unit')
    if (!config.catalogue.units.includes(unit)) return { items: [], error: `Row ${line}: unit "${unit}" is not in Items & pricing settings.` }
    const chargeType = get(r, 'charge_type').toLowerCase() as CatalogueItem['sourceType']
    items.push({
      code: get(r, 'code'), name: get(r, 'name'), description: get(r, 'description') || undefined, category, department, kind: 'Service', unit,
      unitPrice: Math.round(price * 100) / 100, taxId: tax.id,
      taxInclusive: get(r, 'tax_inclusive') ? yes(get(r, 'tax_inclusive')) : undefined,
      discountAllowed: get(r, 'discount_allowed') ? yes(get(r, 'discount_allowed')) : true,
      sourceType: chargeTypes.includes(chargeType) ? chargeType : 'other', matchNames: [], status: 'Active',
    })
  }
  return { items }
}
